import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Tooltip,
  useMediaQuery,
  type SxProps,
  type Theme,
  type TooltipProps,
} from '@mui/material';
import { useIsIPad, useIsMobile } from '../../hooks/mobile-hooks';

/**
 * Copy shown when a user hovers a feature that's intentionally gated out of
 * the public ("lite") app. Kept as a single exported constant so every call
 * site stays in sync.
 */
export const FULL_VERSION_MESSAGE = 'This feature is only available in the full version';

/** Shorter inline caption for disabled menu rows / buttons (agent `ListItemText.secondary` parity). */
export const FULL_VERSION_CAPTION = 'Only available in full version';

/** Agent-style menu secondary line (leading asterisk). */
export const FULL_VERSION_MENU_CAPTION = `*${FULL_VERSION_CAPTION}`;

/**
 * Shared styles for gated/disabled controls. MUI fades disabled IconButtons via
 * `palette.action.disabled` (~26% alpha). Use a mid-range text alpha so icons
 * stay readable but clearly dimmer than enabled toolbar controls (~90% alpha).
 */
export const GATED_DISABLED_SX = {
  opacity: 0.88,
  color: 'rgba(var(--color-text-rgb), 0.52)',
  '&.Mui-disabled': {
    opacity: 0.88,
    color: 'rgba(var(--color-text-rgb), 0.52)',
    WebkitTextFillColor: 'rgba(var(--color-text-rgb), 0.52)',
    '& .MuiSvgIcon-root': {
      color: 'inherit',
    },
  },
} as const;

/** Accent-outline gated buttons (Factsheet / Brochure / Export PDF parity). */
export const GATED_DISABLED_ACCENT_SX = {
  color: 'var(--color-accent)',
  borderColor: 'var(--color-accent)',
  borderWidth: 1.5,
  '&.Mui-disabled': {
    color: 'var(--color-accent)',
    borderColor: 'var(--color-accent)',
    opacity: 0.9,
    WebkitTextFillColor: 'var(--color-accent)',
  },
} as const;

interface FullVersionTooltipProps {
  /**
   * The gated element. When it's a disabled control (button / menu item),
   * MUI can't fire hover events on it directly, so we wrap it in a
   * `<span>` (see `wrap`) which still receives the pointer events the
   * tooltip needs.
   */
  children: React.ReactElement;
  /** Override the default full-version copy if a call site needs to. */
  title?: React.ReactNode;
  placement?: TooltipProps['placement'];
  /**
   * Wrap the child in an inline `<span>` so the tooltip still shows when the
   * child is disabled. Defaults to `true` — the common case here is a
   * disabled button / menu item. Set to `false` when the child already
   * forwards refs + events (e.g. an enabled element).
   */
  wrap?: boolean;
  /** Styling applied to the wrapper span (e.g. `flex: 1` inside a button row). */
  sx?: SxProps<Theme>;
}

/**
 * Reusable tooltip for features that are disabled in the public app. Renders
 * below the gated element on hover (desktop). On mobile / tablet, tap the
 * element to show the tooltip; tap outside to dismiss.
 */
export const FullVersionTooltip: React.FC<FullVersionTooltipProps> = ({
  children,
  title = FULL_VERSION_MESSAGE,
  placement = 'bottom',
  wrap = true,
  sx,
}) => {
  const isMobile = useIsMobile();
  const isIPad = useIsIPad();
  const coarsePointer = useMediaQuery('(hover: none) and (pointer: coarse)');
  const needsTapTooltip = isMobile || isIPad || coarsePointer;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  const handleOpen = useCallback(
    (event: React.MouseEvent) => {
      if (!needsTapTooltip) return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
    },
    [needsTapTooltip],
  );

  const handleClickAway = useCallback((event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  }, []);

  const wrappedChild = wrap ? (
    <Box
      ref={anchorRef}
      component="span"
      sx={{ display: 'inline-flex', ...sx }}
      onClick={needsTapTooltip ? handleOpen : undefined}
    >
      {children}
    </Box>
  ) : (
    children
  );

  const tooltip = (
    <Tooltip
      title={title}
      placement={placement}
      arrow
      open={needsTapTooltip ? open : undefined}
      onClose={() => setOpen(false)}
      disableFocusListener={needsTapTooltip}
      disableHoverListener={needsTapTooltip}
      disableTouchListener
    >
      {wrappedChild}
    </Tooltip>
  );

  if (!needsTapTooltip) {
    return tooltip;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {tooltip}
      </Box>
    </ClickAwayListener>
  );
};

export default FullVersionTooltip;
